import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyDeliveryMethodKey,
  ShopifyDeliveryMethodToFulfillmentOrderKey,
  ShopifyDeliveryMethodToShopKey,
  ShopifyFulfillmentOrderKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyDeliveryMethodKey,
  "2025-04": {
    name: "Shopify Delivery Method",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id", shopifyFieldKey: "id" } },
      },
      {
        name: "Additional Information",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "additionalInformation", shopifyFieldKey: "additionalInformation" },
        },
      },
      {
        name: "Branded Promise",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "brandedPromise", shopifyFieldKey: "brandedPromise" } },
      },
      {
        name: "Max Delivery DateTime",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "maxDeliveryDateTime", shopifyFieldKey: "maxDeliveryDateTime" },
        },
      },
      {
        name: "Min Delivery DateTime",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "minDeliveryDateTime", shopifyFieldKey: "minDeliveryDateTime" },
        },
      },
      {
        name: "Method Type",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "methodType", shopifyFieldKey: "methodType", isShopifyEnum: true },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [
            { name: "LOCAL" },
            { name: "NONE" },
            { name: "PICKUP_POINT" },
            { name: "PICK_UP" },
            { name: "RETAIL" },
            { name: "SHIPPING" },
          ],
        },
      },
      {
        name: "Presented Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "presentedName", shopifyFieldKey: "presentedName" } },
      },
      {
        name: "Service Code",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "serviceCode", shopifyFieldKey: "serviceCode" } },
      },
      {
        name: "Source Reference",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "sourceReference", shopifyFieldKey: "sourceReference" } },
      },
      {
        name: "Fulfillment Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDeliveryMethodToFulfillmentOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "fulfillmentOrderId",
            skipGraphQLSelection: true,
            shopifyFieldKey: "fulfillmentOrderId",
          },
        },
        configuration: {
          relatedModelKey: ShopifyFulfillmentOrderKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDeliveryMethodToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    // synced as part of ShopifyFulfillmentOrder
    syncConfig: { type: "inherit" } as const,
  },
};

export const ShopifyDeliveryMethod = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
